import type { DocumentError } from 'apiConnector/document/document';
import { BlobWithFilename, isBlobWithFilename } from 'apiConnector/document/document';
import type { ErrorDisplayContext } from 'components/shared/Document';
import { DocumentFormat } from 'epic/common-form/Documentation';
import { useAppSelector } from 'hooks/useAppSelector';
import { useCrossBrowserContext } from 'hooks/useCrossBrowserContext';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useTranslation } from 'hooks/useTranslation';
import type { IntlKey } from 'locales';
import { first } from 'lodash-es';
import type { WorkspaceAnalyticCategory } from 'models/analytics';
import type { DocumentType } from 'models/documents/DocumentType';
import { useCallback, useContext } from 'react';
import { createSelector } from 'reselect';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';
import { documentDownloaded, documentDownloadFailed, documentDownloadStart } from 'state/document/actions';
import { hasErrorForType } from 'state/document/selector';
import {
  getDocument,
  getFormInfos,
  getFormProductSubType,
  getFormProductType,
  getIsUserInternal,
  isPendingEnrichment,
} from 'state/selectors';
import { getQuoteExternalIdByWorkspaceTab } from 'state/selectors/common/temp';
import type { AppState } from 'state/state.model';
import type { QuoteWorkspaceType } from 'state/workspaces/model';
import { downloadFile } from 'utils/documents/downloadDocument';

import { DocumentLink } from '../regulatory/design-system/DocumentLink';
import { LanguageLink } from '../regulatory/design-system/LanguageLink';
import { buildLanguages } from './DocumentButton.connect.helper';
import { useGenerateDocument } from './DocumentButton.helper';

interface DocumentLanguage {
  language: string;
  documentFormat?: DocumentFormat;
  isDownloading: boolean;
  error?: DocumentError;
  fileName?: string;
}

interface DocumentButtonData {
  languages: DocumentLanguage[];
  isEnabled: boolean;
  quoteQuery: unknown;
  productSubtype?: string;
  quoteId?: string;
  quoteState: string;
  productType?: string;
  errorContext?: ErrorDisplayContext;
}

const selectDocumentButtonData = createSelector(
  [
    (state: AppState, tabId: string, workspaceType: QuoteWorkspaceType) => getFormInfos(state, workspaceType, tabId),
    (state: AppState) => getIsUserInternal(state),
    (state: AppState, tabId: string) => getFormProductSubType(state, tabId),
    (state: AppState, tabId: string) => getFormProductType(state, tabId),
    (state: AppState, tabId: string, workspaceType: QuoteWorkspaceType) =>
      getQuoteExternalIdByWorkspaceTab(state, workspaceType, tabId),
    (state: AppState, tabId: string) => isPendingEnrichment(state, tabId),
    (state: AppState, tabId: string, _workspaceType: QuoteWorkspaceType, documentType: DocumentType) =>
      hasErrorForType(state, tabId, documentType),
    (state: AppState, tabId: string, workspaceType: QuoteWorkspaceType, documentType: DocumentType) =>
      buildLanguages({
        state,
        tabId,
        documentType,
        documentation: getDocument(state, workspaceType, tabId, documentType),
      }),
  ],
  (
    infos,
    isInternal,
    productSubtype,
    productType,
    quoteId,
    pendingEnrichment,
    hasError,
    languages,
  ): DocumentButtonData => {
    const errorContext = hasError
      ? {
          productSubtype,
          shouldDisplayErrorDetails: isInternal,
          shouldDisplayStandardErrorMessage: !isInternal,
          shouldUseRejectionCode: !isInternal,
        }
      : undefined;

    return {
      languages,
      isEnabled: !pendingEnrichment,
      quoteQuery: infos?.quoteQuery,
      errorContext,
      productSubtype,
      quoteId,
      quoteState: 'Indicative',
      productType,
    };
  },
);

interface Props {
  tabId: string;
  documentType: DocumentType;
  workspaceType: QuoteWorkspaceType;
  className?: string;
}

export function DocumentButton({ tabId, workspaceType, documentType, className }: Props) {
  const { languages, quoteQuery, isEnabled, productSubtype, quoteId, quoteState, productType } = useAppSelector(state =>
    selectDocumentButtonData(state, tabId, workspaceType, documentType),
  );
  const dispatch = useThunkDispatch();
  const label = useTranslation(`form.buttons.${documentType}Button` as IntlKey, documentType);
  const { isDirty, isLocked } = useCrossBrowserContext();
  const isDocumentEnabled = isEnabled && !isDirty && !isLocked;
  const generateDocument = useGenerateDocument(
    quoteQuery,
    documentType,
    productSubtype,
    quoteId,
    quoteState,
    productType,
  );
  const category = useContext(WorkspaceAnalyticCategoryContext);

  const handleDownloadResult = useCallback((result: Blob | BlobWithFilename, fallbackFilename?: string) => {
    if (isBlobWithFilename(result)) {
      return downloadFile(result.blob, result.filename ?? fallbackFilename);
    }
    return downloadFile(result, fallbackFilename);
  }, []);

  const onDownload = useCallback(
    (locale: string, documentFormat: DocumentFormat) => {
      const matchingLanguages = languages.filter(l => l.language === locale);
      const fileName = (matchingLanguages.find(l => l.documentFormat === documentFormat) ?? first(matchingLanguages))
        ?.fileName;

      dispatch(documentDownloadStart(tabId, locale, documentType, documentFormat));

      generateDocument(locale, documentFormat)
        .then(result => handleDownloadResult(result, fileName))
        .then(() => dispatch(documentDownloaded(tabId, locale, documentType, documentFormat)))
        .catch(err => dispatch(documentDownloadFailed(tabId, locale, err, documentType, documentFormat)));

      trackDownload(documentType, category);
    },
    [handleDownloadResult, generateDocument, languages, tabId, documentType, category, dispatch],
  );

  if (languages.length === 0 || !isDocumentEnabled) {
    return null;
  }

  return (
    <DocumentLink label={label} className={className}>
      {languages.map(({ language, documentFormat, isDownloading, error }, index) => {
        if (isDownloading) {
          return <LanguageLink key={index} langCode={language} status="pending" />;
        }

        if (error) {
          return (
            <LanguageLink
              key={index}
              langCode={language}
              status="error"
              severity={error.type === 'TECHNICAL' ? 'warning' : 'danger'}
              onShowError={() => onDownload(language, documentFormat ?? 'PDF')}
            />
          );
        }

        return (
          <LanguageLink
            key={index}
            langCode={language}
            status="success"
            onDownload={() => onDownload(language, documentFormat ?? 'PDF')}
          />
        );
      })}
    </DocumentLink>
  );
}

function trackDownload(documentType: DocumentType | string, category: WorkspaceAnalyticCategory) {
  switch (documentType) {
    case 'Termsheet':
    case 'TermSheet':
      logAnalytics.trackEvent(category, 'Download termsheet');
      break;
    case 'MarketingMaterial':
      logAnalytics.trackEvent(category, 'Download marketing material');
      break;
    case 'Brochure':
      logAnalytics.trackEvent(category, 'Download brochure');
      break;
    case 'FinalTerms':
      logAnalytics.trackEvent(category, 'Download final terms');
      break;
    case 'InformationFactSheet':
      logAnalytics.trackEvent(category, 'Download information fact sheet');
      break;
    case 'SupplementDistributionAgreement':
      logAnalytics.trackEvent(category, 'Download supplement distribution agreement');
      break;
    default:
      break;
  }
}

DocumentButton.displayName = 'DocumentButton';
